// «Mensaje a un área» (shared/API.md §37): the team's área channels, own área first. Picking one
// opens it; when the reader is not a member the client joins first (§17) — the pick IS the explicit
// action, no second confirmation. Paints the last listing at once (memory cache) while it re-asks.
import { useEffect, useMemo, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import { AreaChannelPicker, type AreaChannelOption } from '../../../core/areaChannelPicker';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Dialog, EmptyState, InlineStatus, SearchInput } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';
import { useStableClose } from '../people/dialogFocus';
import '../people/people.css';

export function AreaPickerDialog({ onClose: onCloseProp }: { onClose(): void }) {
  const onClose = useStableClose(onCloseProp);
  const shell = useShell();
  const teamId = shell.team.id;
  const [list, setList] = useState<AreaChannelOption[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState(0);
  const [reloadTick, setReloadTick] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    setFailed(false);
    void data.call('areaChannelsCached', teamId).then((cached) => {
      if (live && cached) setList((l) => l ?? cached);
    });
    void data.call('areaChannels', teamId).then(
      (r) => {
        if (!live) return;
        if ('error' in r) setFailed(true);
        else setList(r.channels);
      },
      () => live && setFailed(true),
    );
    return () => {
      live = false;
    };
  }, [teamId, reloadTick]);

  const visible = useMemo(() => AreaChannelPicker.filter(list ?? [], query), [list, query]);
  useEffect(() => setHighlight(0), [query]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${highlight}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const pick = async (option: AreaChannelOption) => {
    if (busyId) return;
    if (option.joined) {
      onClose();
      shell.openChannel(option.id);
      return;
    }
    setBusyId(option.id);
    setError(null);
    try {
      const result = await data.call('joinChannel', option.id);
      if ('channelId' in result) {
        onClose();
        shell.openChannel(result.channelId);
        return;
      }
    } catch {
      /* said below */
    }
    setError(t('web.area.joinFailed'));
    setBusyId(null);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!visible.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight((h) => (h + step + visible.length) % visible.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const option = visible[Math.min(highlight, visible.length - 1)];
      if (option) void pick(option);
    }
  };

  const footer = error ? (
    <span className="people-footer-hint error" role="alert">
      {error}
    </span>
  ) : undefined;

  return (
    <Dialog title={t('web.area.pickerTitle')} onClose={onClose} footer={footer} width={480}>
      <div className="people-dialog-root">
        <div className="people-sticky" onKeyDown={onKeyDown}>
          <SearchInput value={query} onChange={setQuery} placeholder={t('web.area.search')} autoFocus />
        </div>
        {list === null && !failed ? <InlineStatus kind="loading" text={t('web.people.searching')} /> : null}
        {list === null && failed ? <InlineStatus kind="error" text={t('web.area.failed')} onRetry={() => setReloadTick((n) => n + 1)} /> : null}
        {list && !visible.length ? <EmptyState title={query.trim() ? tf('web.area.noMatches', query.trim()) : t('web.area.empty')} /> : null}
        <div className="people-list" ref={listRef} role="listbox" aria-label={t('web.area.pickerTitle')}>
          {visible.map((option, index) => (
            <div
              key={option.id}
              data-index={index}
              data-area-channel={option.name}
              role="option"
              aria-selected={index === highlight}
              className={`list-row people-row area-row${index === highlight ? ' highlighted' : ''}`}
              onMouseMove={() => index !== highlight && setHighlight(index)}
              onClick={() => void pick(option)}
            >
              <span className="list-row-leading">
                <span className="people-glyph">
                  <Icon name="people" size={16} />
                </span>
              </span>
              <span className="list-row-text">
                <span className="list-row-title">{AreaChannelPicker.rowLine(option)}</span>
                {option.isMine ? <span className="list-row-subtitle">{t('web.area.yours')}</span> : null}
              </span>
              <span className="list-row-trailing">{busyId === option.id ? '…' : <Icon name="chevronRight" size={14} />}</span>
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  );
}
